import { ref, computed, reactive, watch, onBeforeUnmount, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { div, plus, times, lt, lte, minus, min as minNumber, max as maxNumber, toFixed, gt, truncateDecimal } from '@/utils/number';
import { loanApi } from '@/apis/spot';
import { formatLocalTime } from '@/utils/time';
import { toLogin, replace } from '@/locales/i18n';
import { toLogin as toLogin2 } from '@/utils/jumplink';
import { noDmainUrl } from '@/utils/url';
import { toPrecision } from '@/utils/tools';
import { loanPledge } from '@/config/sensors';
import { saPushEvent } from '@/utils/sensors';
import SfeInput from '@/components/Input';
import { accounts } from '@/config/loan';
import KycDialog from './dialog/Kyc';
import LoanDialog from './dialog/Loan';
// import VipDialog from './dialog/Vip';
import FundpoolUnavailable from './dialog/FundpoolUnavailable';
import Process from './loan/Process';
import TokenSelect from './loan/Select';
import AccountSelect from './loan/AccountSelect';
import { errorToast } from '../hooks/useToast';
import { useAssets, stopLoopAssets } from '../hooks/useAssets';
import { useToken, useFormat, usePrecision } from '../hooks/usePrecision';
import { loanTime } from '../utils/time';
import { useTransfer } from '../hooks/useTransfer';
export default {
  components: {
    SfeInput,
    TokenSelect,
    Process,
    KycDialog,
    LoanDialog,
    // VipDialog,
    AccountSelect,
    FundpoolUnavailable
  },
  setup() {
    const {
      t
    } = useI18n({
      useScope: 'global'
    });
    const store = useStore();
    const isLogin = computed(() => store.getters['user/isLogin']);
    const userstate = computed(() => store.state.user.userstate);
    const {
      loanEnabledTokens,
      pledgeEnabledTokens,
      loanTokensMap,
      pledgeTokensMap,
      tokensMap,
      defaultLoanToken
    } = useToken();
    const transferUniframe = useTransfer();
    let unSetPledgeToken = null;
    let unSetLoanToken = null;
    const {
      toFixedLtv,
      toFormatToken,
      toFixedLoan,
      toFormatLoan,
      toFixedPledge,
      toFormatPledge,
      toFixedInterest,
      toFormatInterest
    } = useFormat();
    const {
      loan: loanPrecision,
      pledge: pledgePrecision,
      interest: interestPrecision
    } = usePrecision();
    const loanamount = ref('');
    const pledgeamount = ref('');
    const writedLoanAmount = ref(false);
    const writedPledgeAmount = ref(false);

    // Maximum 按钮是否置灰不可点击
    const isDisabled = ref(false);
    const userInfo = computed(() => store.state.loan.user);
    const tip = `${t('minHourlyIntRate')} <br/> ${t('noteSystemCalculates')}`;
    const state = reactive({
      loanAccount: +localStorage.getItem('by_loan_account') || 0,
      pledgeAccount: +localStorage.getItem('by_pledge_account') || 0,
      loanToken: '',
      loanTokenName: '',
      loanAmountTip: '',
      loanSelectDisabled: false,
      isLoanAmountTip: false,
      pledgeToken: '',
      pledgeTokenName: '',
      limitRange: {},
      pledgeAmountTip: '',
      pledgeAssetTip: '',
      isPledgeAmountTip: false,
      isAssetAmountTip: false,
      totalAmount: '',
      totalInterest: '',
      totalDayInterestRate: '',
      // 每日利息
      qiangpingPrice: '',
      activeLimitIndex: 0,
      transferBalance: '',
      limitOverdueMap: {},
      repayTime: '',
      loanDialogVisible: false,
      vipDialogVisible: false,
      loanDialogData: {},
      vipDialogData: {},
      confirmLoading: false,
      loanRefreshTimer: null,
      currentTime: Date.now(),
      currentAccount: computed(() => store.state.loan.account),
      isSetLoanToken: computed(() => store.state.loan.isSetLoanToken),
      loanAmountNum: 0,
      fundPoolUnavailableText: ''
    });

    // 用户校验
    const checkUser = ref({
      visible: false,
      content: '',
      code: 0,
      confirmText: ''
    });
    const loanTokenInfo = computed(() => loanTokensMap.value[state.loanToken]);
    const pledgeTokenInfo = computed(() => pledgeTokensMap.value[state.pledgeToken]);

    // 质押限额范围
    const limitRange = computed(() => {
      const init = {
        minLoan: '',
        maxLoan: '',
        minPledge: '',
        maxPledge: ''
      };
      const res = state.limitRange[`${state.loanToken}${state.pledgeToken}`];
      if (res && res.success && res.result) {
        return {
          ...init,
          ...res.result
        };
      }
      return init;
    });

    // 质押率
    const usePledgeRate = computed(() => {
      if (!pledgeTokenInfo.value) return {};
      const {
        initialLtv = '',
        marginCall = '',
        liquidationLtv = ''
      } = pledgeTokenInfo.value;
      return {
        initialLtv: toFixedLtv(initialLtv),
        marginCall: toFixedLtv(marginCall),
        liquidationLtv: toFixedLtv(liquidationLtv)
      };
    });

    // 借款期限
    const limitData = computed(() => {
      if (!loanTokenInfo.value) return [];
      const {
        period = ''
      } = loanTokenInfo.value;
      return period ? period.split(',') : [];
    });
    const currentLimit = computed(() => limitData.value[state.activeLimitIndex]);
    const btnText = computed(() => {
      const text = {
        '-1': t('loan_login'),
        1: t('loan_now')
      };
      return text[userstate.value] || '';
    });

    // 利率
    const interestRate = computed(() => {
      const res = {
        hour: '',
        day: '',
        year: ''
      };
      if (!loanTokenInfo.value) return res;
      const {
        hourlyInterestRate: defaultHourlyInterestRate,
        periodConfigList
      } = loanTokenInfo.value;
      const hourlyInterestRate = periodConfigList?.filter?.(item => `${item.period}` === `${currentLimit.value}`)?.[0]?.hourlyInterestRate || defaultHourlyInterestRate;
      const hourlyInterest = times(hourlyInterestRate, 100);
      const dayInterestRate = times(hourlyInterestRate, 24);
      const dayInterest = times(dayInterestRate, 100);
      const yearInterestRate = times(dayInterestRate, 365);
      const yearInterest = times(yearInterestRate, 100);
      return {
        hour: hourlyInterest,
        day: dayInterest,
        year: yearInterest,
        hourRate: hourlyInterestRate,
        dayRate: dayInterestRate,
        yearRate: yearInterestRate
      };
    });

    // 利率
    const interestRateMap = computed(() => {
      const res = {
        hour: '',
        day: '',
        year: ''
      };
      if (!loanTokenInfo.value) return res;
      const {
        hourlyInterestRate: defaultHourlyInterestRate,
        periodConfigList
      } = loanTokenInfo.value;
      const map = {};
      limitData.value.forEach(k => {
        const hourlyInterestRate = periodConfigList?.filter?.(item => `${item.period}` === `${k}`)?.[0]?.hourlyInterestRate || defaultHourlyInterestRate;
        const hourlyInterest = times(hourlyInterestRate, 100);
        const dayInterestRate = times(hourlyInterestRate, 24);
        const dayInterest = times(dayInterestRate, 100);
        const yearInterestRate = times(dayInterestRate, 365);
        const yearInterest = times(yearInterestRate, 100);
        map[k] = {
          hour: hourlyInterest,
          day: dayInterest,
          year: yearInterest,
          hourRate: hourlyInterestRate,
          dayRate: dayInterestRate,
          yearRate: yearInterestRate
        };
      });
      return map;
    });

    // 是否为统一账户并且为资金账户
    const isUFA = computed(() => store.getters['user/isUTA'] && store.getters['user/isFA']);
    const isUTA = computed(() => store.getters['user/isUTA']);
    const isVIP = computed(() => store.getters['user/isVIP']);

    // 默认资产提示
    const initAssetTip = computed(() => {
      if (userstate.value === 1) {
        return t('availableAssetsNewTips', {
          num: toFormatToken(state.transferBalance, state.pledgeToken),
          token: state.pledgeTokenName,
          transferClass: 'homeTransfer',
          depositClass: 'homeDeposit'
        });
      }
      return '';
      // return `${t('loan_available_spot_assets')}: - ${state.pledgeTokenName || ''}`;
    });

    // 期限提示
    const limitTip = computed(() => {
      if (state.limitOverdueMap[currentLimit.value]) {
        const {
          gracePeriod,
          interestMultiple,
          periodType
        } = state.limitOverdueMap[currentLimit.value];
        if (periodType === 2) {
          // 活期
          return t('LoanRateTips1');
        }
        return t('loan_limit_tip', {
          limit: currentLimit.value,
          multiple: interestMultiple,
          time: gracePeriod
        });
      }
      return '';
    });

    // 当前LTV =(借款数量*内盘价格)/(抵押品数量*内盘价格)*100%
    const currentLtv = computed(() => {
      if (!loanamount.value || !pledgeamount.value) {
        return {
          num: '',
          isAbove: false
        };
      }
      if (state.isLoanAmountTip || state.isPledgeAmountTip) {
        return {
          num: '',
          isAbove: false
        };
      }
      const loanValue = loanamount.value.replace(/[^\d.]/g, '');
      const pledgeValue = pledgeamount.value.replace(/[^\d.]/g, '');
      if (+pledgeamount.value === 0) {
        return {
          num: '',
          isAbove: true
        };
      }
      const loanPrice = tokensMap.value[state.loanToken]?.c;
      const pledgePrice = tokensMap.value[state.pledgeToken]?.c;
      const num = toFixed(times(div(times(loanValue, loanPrice), times(pledgeValue, pledgePrice)), 100), 2, 0);
      return {
        num,
        isAbove: lt(usePledgeRate.value.initialLtv, num)
      };
    });

    // 借款按钮禁用状态
    const btnDisabled = computed(() => {
      // 未登录
      if (userstate.value !== 1) {
        return false;
      }
      // 已登录
      if (loanamount.value === '' || pledgeamount.value === '') {
        return false;
      }
      return !state.isLoanAmountTip && !state.isPledgeAmountTip && !state.isAssetAmountTip && !currentLtv.value.isAbove;
    });
    const remainLoanAmount = computed(() => {
      if (!loanTokenInfo.value) return '';
      const {
        maxUserLoanQuota = ''
      } = loanTokenInfo.value;
      const {
        userLoanQuotaUsed = 0
      } = limitRange.value || {};
      const num = toFixedLoan(maxNumber([0, minus(maxUserLoanQuota, userLoanQuotaUsed)]), state.loanToken);
      return {
        num,
        rate: toFixed(times(div(num, maxUserLoanQuota), 100), 2)
      };
    });
    const remainPledgeAmount = computed(() => {
      if (!pledgeTokenInfo.value) return '';
      const {
        maxUserPledgeQuota = ''
      } = pledgeTokenInfo.value;
      const {
        userPledgeQuotaUsed = 0
      } = limitRange.value || {};
      const num = toFixedPledge(maxNumber([0, minus(maxUserPledgeQuota, userPledgeQuotaUsed)]), state.pledgeToken);
      return {
        num,
        rate: toFixed(times(div(num, maxUserPledgeQuota), 100), 2)
      };
    });
    const methods = {
      // 打开 vip 弹出框
      // changeVipDialogVisible(newState) {
      //     if (!isLogin.value) {
      //         toLogin2(window.location.href);
      //         return;
      //     }
      //     // login
      //     this.vipDialogVisible = newState;
      // },
      // 选择借贷账户
      changeLoanAccount(item) {
        state.loanAccount = item;
        localStorage.setItem('by_loan_account', item);
      },
      // 选择质押账户
      changePledgeAccount(item) {
        if (!isLogin.value) {
          return;
        }
        state.pledgeAccount = item;
        localStorage.setItem('by_pledge_account', item);
      },
      // 修改借贷币种
      changeLoanToken(token) {
        state.loanToken = token;
      },
      // 修改质押币种
      changePledgeToken(token) {
        state.pledgeToken = token;
      },
      // 切换币种是否互相包含
      hasSwitchToken() {
        return {
          pledge: pledgeEnabledTokens.value.find(item => item.token === state.loanToken),
          loan: loanEnabledTokens.value.find(item => item.token === state.pledgeToken)
        };
      },
      // 币种方向切换
      switchLoanPledge() {
        // 其他账户&币种有效，则不可切换
        if (state.loanSelectDisabled) {
          return;
        }
        state.activeLimitIndex = 0;
        let changedLoanToken = state.loanToken;
        let changedPledgeToken = state.pledgeToken;
        const hasToken = methods.hasSwitchToken();
        // 两侧都不包含，则不交换
        if (!hasToken.loan && !hasToken.pledge) {
          return;
        }
        if (hasToken.loan && !hasToken.pledge && pledgeEnabledTokens.value.length > 1) {
          // 借贷包含，质押不包含
          const filterPeldge = pledgeEnabledTokens.value.filter(item => item.token !== state.pledgeToken);
          changedLoanToken = state.pledgeToken;
          changedPledgeToken = filterPeldge[0]?.token;
        } else if (hasToken.pledge && !hasToken.loan && loanEnabledTokens.value.length > 1) {
          // 质押包含，借贷不包含
          const filterLoan = loanEnabledTokens.value.filter(item => item.token !== state.loanToken);
          changedLoanToken = filterLoan[0]?.token;
          changedPledgeToken = state.loanToken;
        } else if (hasToken.loan && hasToken.pledge) {
          // 两者都包含
          changedLoanToken = state.pledgeToken;
          changedPledgeToken = state.loanToken;
        }
        methods.changeLoanToken(changedLoanToken);
        methods.changePledgeToken(changedPledgeToken);
        saPushEvent(loanPledge.reverse_coin, {
          page_name: loanPledge.home,
          borrowing_token: changedLoanToken,
          pledge_token: changedPledgeToken
        });
      },
      // 获取质押币种的最小最大质押金额
      async getPledgeLimit() {
        const {
          loanToken,
          pledgeToken
        } = state;
        const result = await loanApi.getTokenRange({
          loanToken,
          pledgeToken
        });
        state.limitRange[`${loanToken}${pledgeToken}`] = result;
        // 资金池不可用，弹窗提示
        if (!result?.result?.fundPoolAvailable && !result?.result?.pledgeQuotaAvailable) {
          state.fundPoolUnavailableText = t('exceedLimitError');
        } else if (!result?.result?.fundPoolAvailable) {
          state.fundPoolUnavailableText = t('insufficientLendingPoolDemand');
        } else if (!result?.result?.pledgeQuotaAvailable) {
          state.fundPoolUnavailableText = t('exceedPlatformLimit');
        } else {
          state.fundPoolUnavailableText = '';
        }
      },
      // 查询逾期参数
      async getOverdueInfo() {
        const res = await loanApi.getOverdueInfo();
        if (res.success) {
          const overdueParameter = res.result;
          overdueParameter.forEach(item => {
            state.limitOverdueMap[item.loanPeriod] = item;
          });
        }
      },
      // 借贷金额输入
      inputLoanAmount() {
        writedLoanAmount.value = true;
        methods.setPledgeAmount();
      },
      // 质押金额输入
      inputPledgeAmount() {
        writedPledgeAmount.value = true;
        methods.setLoanAmount();
      },
      // 根据借贷金额计算质押金额
      setPledgeAmount() {
        if (!pledgeamount.value) writedPledgeAmount.value = false; // 清空质押金额时，要恢复自动计算，清除手动输入状态
        if (writedPledgeAmount.value) return; // 已手动输入，不自动修改
        // 币种汇率 = 借贷币种场内/质押币种场内
        // 质押金额 = 借款金额 * 币种汇率 / 质押币种初始质押率
        const loanValue = loanamount.value.replace(/[^\d.]/g, '');
        const initialLtv = div(usePledgeRate.value?.initialLtv, 100);
        const tokenRate = div(tokensMap.value[state.loanToken]?.c, tokensMap.value[state.pledgeToken]?.c);
        pledgeamount.value = loanValue ? toFixedPledge(div(times(loanValue, tokenRate), initialLtv), state.pledgeToken, 0) : '';
      },
      // 根据质押金额计算借贷金额
      setLoanAmount() {
        if (!loanamount.value) writedLoanAmount.value = false; // 清空借贷金额时，要恢复自动计算，清除手动输入状态
        if (writedLoanAmount.value) return; // 已手动输入，不自动修改

        // 币种汇率 = 借贷币种场内/质押币种场内
        // 借款金额 = 质押金额 * 质押币种初始质押率 / 币种汇率
        const pledgeValue = pledgeamount.value.replace(/[^\d.]/g, '');
        const initialLtv = div(usePledgeRate.value?.initialLtv, 100);
        const tokenRate = div(tokensMap.value[state.loanToken]?.c, tokensMap.value[state.pledgeToken]?.c);
        loanamount.value = pledgeValue ? toFixedLoan(div(times(pledgeValue, initialLtv), tokenRate), state.loanToken) : '';
      },
      // 校验借贷金额
      checkLoanAmount() {
        const {
          token,
          tokenName
        } = loanTokenInfo.value;
        const {
          minLoan
        } = limitRange.value;
        if (lt(loanamount.value, toFixedLoan(minLoan, token, 0))) {
          state.isLoanAmountTip = true;
          state.loanAmountTip = t('loan_min_amount', {
            num: toFormatLoan(minLoan, token, 0),
            token: tokenName
          });
          return;
        }
        // 用户使用完额度后，最小值也为0，这时增加校验输入值不能为0
        if (lte(loanamount.value, 0)) {
          state.isLoanAmountTip = true;
          state.loanAmountTip = t('borrowAmountNotZero');
          return;
        }
        state.isLoanAmountTip = false;
        state.loanAmountTip = '';
      },
      // 校验质押金额
      checkPledgeAmount() {
        if (!pledgeTokenInfo.value) {
          return;
        }
        const {
          token,
          tokenName
        } = pledgeTokenInfo.value;
        const {
          minPledge
        } = limitRange.value;
        if (lt(state.transferBalance, pledgeamount.value)) {
          state.isAssetAmountTip = true;
          state.pledgeAssetTip = t('InsufficientAvailableAssetsTips', {
            transferClass: 'homeTransfer',
            depositClass: 'homeDeposit',
            num: toFormatToken(state.transferBalance, state.pledgeToken),
            token: tokenName
          });
        } else {
          state.isAssetAmountTip = false;
          state.pledgeAssetTip = state.initAssetTip;
        }
        if (lt(pledgeamount.value, toFixedPledge(minPledge, token, 0))) {
          state.isPledgeAmountTip = true;
          state.pledgeAmountTip = t('loan_min_amount', {
            num: toFormatPledge(minPledge, token, 0),
            token: tokenName
          });
          return;
        }
        state.isPledgeAmountTip = false;
        state.pledgeAmountTip = '';
      },
      // 校验用户
      checkUserAccount(type) {
        if (type === 'kyc') {
          // 完成基础认证
          checkUser.value = {
            visible: true,
            type,
            content: t('loan_kyc_tip'),
            confirmText: t('confirm')
          };
          return false;
        }
        if (type === 'account') {
          // 联系客服
          checkUser.value = {
            visible: true,
            type,
            content: t('loan_account_tip'),
            confirmText: t('contactOnline')
          };
          return false;
        }
        return true;
      },
      // 立即借币
      async confirmLoan() {
        saPushEvent(loanPledge.loan_btn, {
          page_name: loanPledge.home,
          plate_area: 'Borrowing',
          borrowing_token: state.loanToken,
          pledge_token: state.pledgeToken,
          loan_period: currentLimit.value
        });
        // 未登录
        if (userstate.value !== 1) {
          // 跳转登录
          toLogin();
          return;
        }
        // 已登录
        // 借款金额校验
        const {
          maxLoan
        } = limitRange.value;
        if (lt(toFixedLoan(maxLoan, state.loanToken), loanamount.value)) {
          errorToast(t('exceedPlatformBorrowLimit'));
          return;
        }
        // 质押金额校验
        const {
          maxPledge
        } = limitRange.value;
        if (lt(toFixedPledge(maxPledge, state.pledgeToken), pledgeamount.value)) {
          errorToast(t('exceedPlatformLimit'));
          return;
        }
        // 准入校验
        state.confirmLoading = true;
        const res = await loanApi.postCheckUser();
        state.confirmLoading = false;
        if (res.success) {
          let {
            systemTime
          } = res.result;
          state.loanDialogVisible = true;
          state.currentTime = Date.now();
          const key = limitData.value[state.activeLimitIndex];
          state.loanDialogData = {
            loanToken: state.loanToken,
            loanTokenName: state.loanTokenName,
            loanamount: loanamount.value.replace(/[^\d.]/g, ''),
            totalInterest: state.totalInterest,
            dayInterestRate: interestRate.value.dayRate,
            // yearInterestRate,
            pledgeamount: pledgeamount.value.replace(/[^\d.]/g, ''),
            pledgeToken: state.pledgeToken,
            pledgeTokenName: state.pledgeTokenName,
            limit: currentLimit.value,
            totalAmount: state.totalAmount,
            loanPrecision: loanPrecision(state.loanToken),
            pledgePrecision: pledgePrecision(state.pledgeToken),
            interestPrecision: interestPrecision(state.loanToken),
            repayTime: loanTime(Number(systemTime), currentLimit.value),
            account: state.currentAccount,
            targetAccountType: state.loanAccount,
            accountType: state.pledgeAccount,
            periodType: state.limitOverdueMap?.[key]?.periodType
          };

          // 每10s更新质押金额数据&应还款时间
          state.loanRefreshTimer = setInterval(() => {
            methods.setPledgeAmount();
            const now = Date.now();
            systemTime = plus(systemTime, minus(now, state.currentTime));
            state.currentTime = now;
            state.loanDialogData = {
              ...state.loanDialogData,
              pledgeamount: pledgeamount.value.replace(/[^\d.]/g, ''),
              repayTime: loanTime(Number(systemTime), currentLimit.value)
            };
          }, 10000);
          return;
        }
        const {
          code,
          ret_msg: errMsg,
          message
        } = res;
        if (code === 100501 || code === 100502) {
          const type = code === 100501 ? 'kyc' : 'account';
          methods.checkUserAccount(type);
          return;
        }
        if (errMsg || message) {
          errorToast(errMsg || message);
        }
      },
      // 借款成功
      handleSuccess() {
        // 清空输入框，更新最大最小金额，更新订单数量，tab切换至订单
        loanamount.value = '';
        pledgeamount.value = '';
        store.dispatch('loan/GET_PROGRESS_ORDER_QTY');
        methods.getPledgeLimit();
        store.commit('loan/SET_TAB', 'Order');
      },
      handleVip() {},
      // 划转充值
      handleAssets(e) {
        if (e.target.id === 'transfer') {
          if (!isLogin.value) {
            toLogin2(window.location.href);
            return;
          }
          transferUniframe.visible(state.pledgeToken, isUTA.value, state.pledgeAccount);
          return;
        }
        if (e.target.id === 'deposit' || e.target.id === 'Deposit') {
          window.open(`/user/assets/home/${isUFA.value ? 'fiat' : 'spot'}`);
        }
      },
      // 设置最大质押
      setMaxPledge() {
        if (isDisabled.value) {
          return;
        }
        if (isLogin.value) {
          const maxPledge = remainPledgeAmount.value.num;
          const {
            transferBalance
          } = state;
          pledgeamount.value = toFixedPledge(maxNumber([minNumber([maxPledge, transferBalance]), '0']), state.pledgeToken);
          methods.setLoanAmount();
        }
        if (!isLogin.value && limitRange.value?.maxPledge !== '') {
          pledgeamount.value = toFixedPledge(limitRange.value?.maxPledge, state.pledgeToken);
          methods.setLoanAmount();
        }
      }
    };

    // 设置Maximum按钮状态
    watch([() => limitRange.value.minPledge, () => remainPledgeAmount.value.num, () => isLogin.value], ([minPledge, maxAmount, login]) => {
      if (login) {
        if (lt(maxAmount, minPledge)) {
          isDisabled.value = true;
        } else {
          isDisabled.value = false;
        }
      } else {
        isDisabled.value = false;
      }
    }, {
      immediate: true
    });
    // 设置初始借贷币种
    unSetLoanToken = watch([() => defaultLoanToken.value, () => state.isSetLoanToken], () => {
      if (state.isSetLoanToken && Object.keys(defaultLoanToken.value ?? {}).length) {
        state.loanToken = defaultLoanToken.value.token;
        state.loanSelectDisabled = defaultLoanToken.value.disabled;
        unSetLoanToken?.();
      }
    }, {
      immediate: true
    });

    // 设置初始质押币种
    unSetPledgeToken = watch([() => state.loanToken, () => pledgeEnabledTokens.value?.length], ([loan, len]) => {
      if (loan && len) {
        const filterPledgeTokens = pledgeEnabledTokens.value.filter(item => item.token !== loan);
        state.pledgeToken = filterPledgeTokens[0]?.token;
        unSetPledgeToken?.();
      }
    }, {
      immediate: true
    });

    // 币种改变，清空数据
    watch([() => state.loanToken, () => state.pledgeToken], ([loanToken, pledgeToken]) => {
      loanamount.value = '';
      pledgeamount.value = '';
      if (loanToken && pledgeToken) {
        // 限额范围
        methods.getPledgeLimit();
      }
    }, {
      immediate: true
    });
    watch(() => state.loanAccount, newAccount => {
      if (newAccount === 0) {
        return;
      }
      store.dispatch('loan/GET_LOAN_TOKENS', {
        accountType: newAccount
      });
    }, {
      immediate: true
    });

    // 设置币种名称
    watch([() => loanTokenInfo.value, () => pledgeTokenInfo.value], () => {
      state.loanTokenName = loanTokenInfo.value?.tokenName || '';
      state.pledgeTokenName = pledgeTokenInfo.value?.tokenName || '';
    }, {
      immediate: true
    });

    // 轮询获取质押币种可用资产
    watch([() => state.pledgeToken, () => userstate.value, () => state.pledgeAccount], () => {
      if (userstate.value === 1) {
        const {
          transferBalance
        } = useAssets(state.pledgeToken, state.pledgeAccount);
        state.transferBalance = transferBalance;
      }
    }, {
      immediate: true
    });

    // 借贷金额+期限改变，更新数据
    watch([() => loanamount.value, () => state.activeLimitIndex, () => pledgeamount.value], () => {
      methods.checkLoanAmount();
      // 总利息 = 借款金额*利率*limit*24 借贷币种精度
      // 总还款 = 借款金额+借款金额*利率*limit*24 借贷币种精
      const dayInterestRate = interestRate.value.dayRate;
      state.totalInterest = times(times(loanamount.value, currentLimit.value), dayInterestRate);
      state.totalAmount = plus(loanamount.value, state.totalInterest);
      state.totalDayInterestRate = times(loanamount.value, dayInterestRate);
      const interest = state.limitOverdueMap?.[limitData.value[state.activeLimitIndex]]?.periodType !== 2 ? state.totalInterest : 0;
      state.qiangpingPrice = div(plus(loanamount.value, interest), times(pledgeamount.value, usePledgeRate.value.liquidationLtv / 100));
    });

    // 质押金额改变，校验数据
    watch([() => pledgeamount.value, () => state.transferBalance], () => {
      methods.checkPledgeAmount();
    });
    watch(() => state.loanDialogVisible, value => {
      if (!value) {
        clearInterval(state.loanRefreshTimer);
      }
    });

    // 查询期限逾期参数
    methods.getOverdueInfo();
    onBeforeUnmount(() => {
      // 终止轮询质押币种可用资产
      stopLoopAssets();
      // 终止轮询更新借款弹层确认数据
      clearInterval(state.loanRefreshTimer);
    });
    return {
      t,
      ...toRefs(state),
      ...methods,
      userstate,
      loanEnabledTokens,
      loanamount,
      loanTokenInfo,
      pledgeEnabledTokens,
      pledgeamount,
      initAssetTip,
      pledgeTokenInfo,
      limitData,
      limitTip,
      currentLimit,
      btnText,
      btnDisabled,
      interestRate,
      interestRateMap,
      usePledgeRate,
      checkUser,
      limitRange,
      accounts,
      isUFA,
      times,
      replace,
      formatLocalTime,
      loanPrecision,
      pledgePrecision,
      interestPrecision,
      toFixedLoan,
      toFormatLoan,
      toFixedPledge,
      toFormatPledge,
      toFixedInterest,
      toFormatInterest,
      noDmainUrl,
      toPrecision,
      isDisabled,
      tip,
      toFormatToken,
      toFixed,
      gt,
      truncateDecimal,
      isVIP,
      remainLoanAmount,
      remainPledgeAmount,
      currentLtv,
      lt
    };
  }
};